import { useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Paperclip, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useReply, useUploadAttachment } from '@/queries/ticketMutations'
import { loadDraft, clearDraft, useDraftPersist } from '@/hooks/useDraft'
import { isAdminViewer } from '@/lib/scope'
import type { TicketStatus, ViewerRelation } from '@/types/ticket'
import { toast } from 'sonner'

const MAX_SIZE = 25 * 1024 * 1024

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  ticketId: string
  status: TicketStatus
  viewer: ViewerRelation
  initialBody?: string
}

export function ReplyComposer({ ticketId, status, viewer, initialBody }: Props) {
  const { user } = useAuth0()
  const reply = useReply(ticketId, { name: user?.name, isAdmin: isAdminViewer(viewer) })
  const upload = useUploadAttachment(ticketId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Draft survives navigation/refresh; cleared on successful send. An
  // investigation's "Use as reply" seed (via the composer's key remount)
  // takes priority over a stale localStorage draft.
  const [body, setBody] = useState(() => initialBody ?? loadDraft(ticketId))
  const [isInternal, setIsInternal] = useState(false)
  useDraftPersist(ticketId, body)
  const [attachments, setAttachments] = useState<Array<{ s3Key: string; filename: string; mimeType: string; size: number }>>([])
  const closed = status === 'closed'
  const isAdmin = isAdminViewer(viewer)

  async function handleFile(file: File | null | undefined) {
    if (!file) return
    if (file.size > MAX_SIZE) {
      toast.error(`${file.name} is over 25MB.`)
      return
    }
    try {
      const meta = await upload.mutateAsync(file)
      setAttachments(prev => [...prev, meta])
    } catch (err) {
      toast.error((err as Error).message ?? 'Upload failed')
    }
  }

  if (closed) {
    return (
      <div className="p-4 border-t text-sm text-muted-foreground">
        This ticket is closed — admin must reopen.
      </div>
    )
  }

  return (
    <form
      onSubmit={async e => {
        e.preventDefault()
        if (!body.trim()) return
        try {
          await reply.mutateAsync({ body, attachments, isInternal: isAdmin && isInternal })
          setBody(''); setAttachments([]); setIsInternal(false)
          clearDraft(ticketId)
        } catch (err) {
          toast.error((err as Error).message ?? 'Reply failed')
        }
      }}
      className={`p-4 border-t space-y-2 ${isAdmin && isInternal ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}
      aria-label="Reply"
    >
      <Textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Type your reply…" />

      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <li
              key={a.s3Key}
              className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/50 px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 text-muted-foreground" />
              <span className="max-w-[180px] truncate">{a.filename}</span>
              <span className="text-muted-foreground">{formatSize(a.size)}</span>
              <button
                type="button"
                aria-label={`Remove ${a.filename}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        aria-label="Attach file"
        onChange={e => {
          handleFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Paperclip className="h-3.5 w-3.5" />
          )}
          Attach
        </Button>
        {isAdmin && (
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
            Internal note (hidden from customer)
          </label>
        )}
        <Button type="submit" disabled={reply.isPending || !body.trim()} className="ml-auto">
          {reply.isPending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </form>
  )
}
